import { useContext, type VoidFunctionComponent } from 'react';
import ContentLoader from 'react-content-loader';
import { Link } from 'react-router';

import { LoadingBoundary } from '../../../components/generic/LoadingBoundary';
import Text from '../../../components/generic/Text';
import { RouterContext } from '../../../contexts/RouterContext';
import COLORS from '../../../theme/colors';
import { containEvent } from '../../../utils';
import { releaseGroupReleaseRoute } from '../../helpers/routes';
import useSlug from '../../hooks/useSlug';
import type { ReleaseGroupReleasesResponse } from '../../../../shared/publicPortal/types';
import type { LoadingBoundaryProps } from '../../../components/generic/LoadingBoundary';
import type { AsyncValue } from '../../../redux/asyncValue';

import styles from './ReleaseGroup.module.less';

const VersionLoader = () => (
  <div className="block">
    <ContentLoader
      speed={2}
      width={150}
      height={144}
      foregroundColor={COLORS.fossaGray05}
      backgroundColor={COLORS.fossaGray10}
    >
      <rect x="0" y="8" rx="3" ry="3" width="150" height="28" />
      <rect x="0" y="44" rx="3" ry="3" width="150" height="28" />
      <rect x="0" y="80" rx="3" ry="3" width="150" height="28" />
      <rect x="0" y="116" rx="3" ry="3" width="150" height="28" />
    </ContentLoader>
  </div>
);

const VersionContent: LoadingBoundaryProps<ReleaseGroupReleasesResponse>['renderValue'] = ({ value }) => {
  const slug = useSlug();
  const { replacePath, getLocation } = useContext(RouterContext);
  const { pathname } = getLocation();
  const route = (id: number) => releaseGroupReleaseRoute(slug, value.releaseGroup.id, id);

  return (
    <div className={styles.versionList}>
      {value.releases.map((release) => {
        const releaseRoute = route(release.id);
        const isActive = pathname.startsWith(releaseRoute);
        return (
          <Link
            key={release.id}
            to={route(release.id)}
            className={isActive ? styles.activeVersion : ''}
            onClick={containEvent(() => {
              replacePath(route(release.id));
            })}
          >
            {release.title}
          </Link>
        );
      })}
    </div>
  );
};

type VersionListProps = {
  releases: AsyncValue<ReleaseGroupReleasesResponse>;
};

const VersionList: VoidFunctionComponent<VersionListProps> = ({ releases }) => (
  <div className={styles.versions}>
    <Text.H4>Versions</Text.H4>
    <LoadingBoundary
      asyncValue={releases}
      renderValue={VersionContent}
      renderLoading={<VersionLoader />}
      renderEmpty={<VersionLoader />}
      renderError={VersionLoader}
    />
  </div>
);

export default VersionList;
